// ChatGPT Ads (OpenAI Measurement Pixel, loaded in shared/tracking.twig).
// User data rules: https://developers.openai.com/ads/measurement-pixel#send-user-data

export const sha256 = async (value) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const normaliseName = (name) => name.toLowerCase().replace(/[\s!-/:-@[-`{-~]/g, '');

// ponytail: local-format numbers (e.g. 07700 900123) are skipped as we don't know their country code;
// normalise server-side with libphonenumber if phone matching matters.
const normalisePhone = (phone) => {
  const digits = /^\s*(\+|00)/.test(phone) ? phone.replace(/\D/g, '').replace(/^0+/, '') : '';
  return /^\d{8,15}$/.test(digits) ? digits : '';
};

async function sendUser({ email, fullName, phone }) {
  const account = window.__TGV_USER_DATA?.type === 'user' ? window.__TGV_USER_DATA : {};
  const name = fullName || account.fullName;
  const nameParts = (value) => value.trim().split(/\s+/);
  const fields = {
    email_sha256: [(value) => value.trim().toLowerCase(), email || account.email],
    phone_number_sha256: [normalisePhone, phone || account.phoneNumber],
    external_id_sha256: [(value) => value.trim(), account.id],
    first_name_sha256: [(value) => normaliseName(nameParts(value)[0]), name],
    last_name_sha256: [(value) => (nameParts(value).length > 1 ? normaliseName(nameParts(value).at(-1)) : ''), name],
  };

  const user = {};
  for (const [key, [normalise, value]] of Object.entries(fields)) {
    try {
      const normalised = value ? normalise(String(value)) : '';
      if (normalised) {
        user[key] = await sha256(normalised);
      }
    } catch {
      // Skip just this field (e.g. no crypto.subtle on old browsers) – the conversion is still measured.
    }
  }

  if (Object.keys(user).length > 0) {
    window.oaiq('init', { user });
  }
}

let identified = Promise.resolve();

// Call when the Client submits the enquiry. Hashing is async and success handlers may redirect right after
// trackChatGptRfpSent(), so the hashes must be ready by then; `init` must come before `measure` to attach them.
export function identifyChatGptUser(user = {}) {
  if (window.oaiq) {
    identified = sendUser(user);
  }

  return identified;
}

// `amount` is in minor units: the same GBP value we send to GA4 as `value`, times 100.
export function trackChatGptRfpSent(estimatedValue) {
  const data = { type: 'contents' };

  if (estimatedValue !== null && estimatedValue !== undefined) {
    data.amount = Math.round(estimatedValue * 100);
    data.currency = 'GBP';
  }

  const measure = () => {
    // The "Enquiry Sent" conversion goal of the existing campaigns listens to this custom event
    // and goals can't be changed on a live campaign, so it stays next to the standard event.
    window.oaiq?.('measure', 'custom', { type: 'custom' }, { custom_event_name: 'enquiry-sent' });
    window.oaiq?.('measure', 'order_created', data);
  };

  // Already settled by now, so this runs as a microtask – before any redirect can unload the page.
  return identified.then(measure, measure);
}
